import { useState, useEffect, useCallback, useRef } from 'react';

export default function useLocalStorage<ValueType>(
    key: string,
    defaultValue: ValueType
): [ValueType, (newValue: ValueType | ((val: ValueType) => ValueType)) => void] {
    const defaultValueRef = useRef(defaultValue);
    defaultValueRef.current = defaultValue;

    const [value, setValue] = useState<ValueType>(() => {
        if (typeof window === 'undefined') {
            return defaultValue;
        }

        try {
            const storedValue = localStorage.getItem(key);
            return storedValue === null ? defaultValue : JSON.parse(storedValue);
        } catch {
            return defaultValue;
        }
    });

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const listener = (e: StorageEvent) => {
            if (e.storageArea === localStorage && e.key === key) {
                try {
                    const newValue = e.newValue ? JSON.parse(e.newValue) : defaultValueRef.current;
                    setValue(newValue);
                } catch {
                    setValue(defaultValueRef.current);
                }
            }
        };

        window.addEventListener('storage', listener);

        return () => {
            window.removeEventListener('storage', listener);
        };
    }, [key]);

    const setValueInLocalStorage = useCallback(
        (newValue: ValueType | ((val: ValueType) => ValueType)) => {
            setValue(currentValue => {
                const result =
                    typeof newValue === 'function'
                        ? (newValue as (val: ValueType) => ValueType)(currentValue)
                        : newValue;

                if (typeof window !== 'undefined') {
                    try {
                        localStorage.setItem(key, JSON.stringify(result));
                    } catch (error) {
                        console.error(error);
                    }
                }

                return result;
            });
        },
        [key]
    );

    return [value, setValueInLocalStorage];
}
